import { Injectable, signal, computed } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface CartItemCustomization {
  type: 'remove' | 'add' | 'change';
  ingredient: string;
  description: string; // Ejemplo: "sin lechuga", "extra queso", "salsa picante"
}

export interface CartItem {
  id: string; // Unique ID for cart item (product_id + customizations hash)
  product_id: string;
  name: string;
  price: number;
  quantity: number;
  customizations: CartItemCustomization[];
  notes: string; // Notas adicionales del cliente
  image?: string;
  category: string;
  // Pack info (for items added via pack wizard)
  pack_id?: number;
  pack_name?: string;
  real_product_id?: number; // The actual product_id (numeric) for backend
  // Variant info
  variant_id?: number;
  variant_name?: string;
  // Extra prices (stored for order submission)
  extra_prices?: { name: string; price: number }[];
}

export type OrderMode = 'dine-in' | 'takeaway' | 'delivery';

export interface Cart {
  items: CartItem[];
  restaurant_id: string;
  restaurant_name: string;
  terminal_suffix: string;
  order_mode: OrderMode;
  created_at: Date;
  updated_at: Date;
}

@Injectable({
  providedIn: 'root'
})
export class CartService {
  // Signals para estado reactivo
  private cartSignal = signal<Cart | null>(null);
  
  // Computed values
  cart = computed(() => this.cartSignal());
  
  totalItems = computed(() => {
    const cart = this.cartSignal();
    return cart ? cart.items.reduce((total, item) => total + item.quantity, 0) : 0;
  });
  
  totalPrice = computed(() => {
    const cart = this.cartSignal();
    return cart ? cart.items.reduce((total, item) => total + (item.price * item.quantity), 0) : 0;
  });
  
  isEmpty = computed(() => this.totalItems() === 0);

  constructor() {
    // Cargar carrito desde localStorage al inicializar
    this.loadCartFromStorage();
  }

  /**
   * Inicializar un nuevo carrito para un restaurante
   */
  /**
   * Establecer modo de pedido
   */
  setOrderMode(mode: OrderMode): void {
    const currentCart = this.cartSignal();
    if (currentCart) {
      this.cartSignal.set({ ...currentCart, order_mode: mode, updated_at: new Date() });
      this.saveCartToStorage();
    }
    // Store mode for next cart initialization
    localStorage.setItem('barclik_order_mode', mode);
  }

  /**
   * Obtener modo de pedido actual
   */
  getOrderMode(): OrderMode {
    const currentCart = this.cartSignal();
    if (currentCart) return currentCart.order_mode;
    return (localStorage.getItem('barclik_order_mode') as OrderMode) || 'dine-in';
  }

  /**
   * Inicializar un nuevo carrito para un restaurante
   */
  initializeCart(restaurant_id: string, restaurant_name: string, terminal_suffix: string): void {
    const storedMode = (localStorage.getItem('barclik_order_mode') as OrderMode) || 'dine-in';
    const newCart: Cart = {
      items: [],
      restaurant_id,
      restaurant_name,
      terminal_suffix,
      order_mode: storedMode,
      created_at: new Date(),
      updated_at: new Date()
    };
    
    this.cartSignal.set(newCart);
    this.saveCartToStorage();
    
    console.log('🛒 Initialized new cart for:', restaurant_name);
  }

  /**
   * Añadir item al carrito
   */
  addItem(
    product_id: string,
    name: string,
    price: number,
    category: string,
    customizations: CartItemCustomization[] = [],
    notes: string = '',
    image?: string,
    extra?: {
      pack_id?: number;
      pack_name?: string;
      real_product_id?: number;
      variant_id?: number;
      variant_name?: string;
      extra_prices?: { name: string; price: number }[];
    }
  ): void {
    const currentCart = this.cartSignal();

    if (!currentCart) {
      console.error('❌ No active cart to add items to');
      return;
    }

    // Generar ID único basado en producto y personalizaciones
    const itemId = this.generateItemId(product_id, customizations, notes);

    // Buscar si ya existe este item exacto
    const existingItemIndex = currentCart.items.findIndex(item => item.id === itemId);

    if (existingItemIndex >= 0) {
      // Si existe, incrementar cantidad
      currentCart.items[existingItemIndex].quantity += 1;
      console.log('➕ Increased quantity for existing item:', name);
    } else {
      // Si no existe, añadir nuevo item
      const newItem: CartItem = {
        id: itemId,
        product_id,
        name,
        price,
        quantity: 1,
        customizations,
        notes,
        image,
        category,
        ...(extra?.pack_id != null && { pack_id: extra.pack_id }),
        ...(extra?.pack_name && { pack_name: extra.pack_name }),
        ...(extra?.real_product_id != null && { real_product_id: extra.real_product_id }),
        ...(extra?.variant_id != null && { variant_id: extra.variant_id }),
        ...(extra?.variant_name && { variant_name: extra.variant_name }),
        ...(extra?.extra_prices?.length && { extra_prices: extra.extra_prices }),
      };

      currentCart.items.push(newItem);
      console.log('🛒 Added new item to cart:', name);
    }

    currentCart.updated_at = new Date();
    this.cartSignal.set({ ...currentCart });
    this.saveCartToStorage();
  }

  /**
   * Actualizar cantidad de un item
   */
  updateQuantity(itemId: string, quantity: number): void {
    const currentCart = this.cartSignal();
    if (!currentCart) return;

    const itemIndex = currentCart.items.findIndex(item => item.id === itemId);
    
    if (itemIndex >= 0) {
      if (quantity <= 0) {
        // Si cantidad es 0 o menor, eliminar item
        this.removeItem(itemId);
      } else {
        currentCart.items[itemIndex].quantity = quantity;
        currentCart.updated_at = new Date();
        this.cartSignal.set({ ...currentCart });
        this.saveCartToStorage();
        
        console.log('🔢 Updated quantity for item:', currentCart.items[itemIndex].name, 'to:', quantity);
      }
    }
  }

  /**
   * Obtener cantidad de un producto en el carrito
   */
  getItemQuantity(productId: string): number {
    const currentCart = this.cartSignal();
    if (!currentCart) return 0;

    // Buscar todos los items con el mismo product_id y sumar las cantidades
    return currentCart.items
      .filter(item => item.product_id === productId)
      .reduce((total, item) => total + item.quantity, 0);
  }

  /**
   * Eliminar item del carrito (reduce cantidad en 1)
   */
  removeItem(itemId: string): void {
    const currentCart = this.cartSignal();
    if (!currentCart) return;

    const itemIndex = currentCart.items.findIndex(item => item.id === itemId);
    
    if (itemIndex >= 0) {
      const item = currentCart.items[itemIndex];
      
      if (item.quantity > 1) {
        // Si tiene más de 1, reducir cantidad
        item.quantity -= 1;
        currentCart.updated_at = new Date();
        this.cartSignal.set({ ...currentCart });
        this.saveCartToStorage();
        console.log('➖ Reduced quantity for item:', item.name, 'to:', item.quantity);
      } else {
        // Si tiene 1, eliminar completamente
        currentCart.items.splice(itemIndex, 1);
        currentCart.updated_at = new Date();
        this.cartSignal.set({ ...currentCart });
        this.saveCartToStorage();
        console.log('🗑️ Removed item from cart:', item.name);
      }
    }
  }

  /**
   * Eliminar todos los items de un pack (por prefijo de product_id, e.g. "pack-5")
   */
  removePackGroup(packPrefix: string): void {
    const currentCart = this.cartSignal();
    if (!currentCart) return;

    const before = currentCart.items.length;
    currentCart.items = currentCart.items.filter(i => !i.product_id.startsWith(packPrefix + '-'));
    if (currentCart.items.length < before) {
      currentCart.updated_at = new Date();
      this.cartSignal.set({ ...currentCart });
      this.saveCartToStorage();
      console.log('🗑️ Removed pack group:', packPrefix);
    }
  }

  /**
   * Vaciar carrito completo
   */
  clearCart(): void {
    const currentCart = this.cartSignal();
    if (!currentCart) return;

    const clearedCart: Cart = {
      ...currentCart,
      items: [],
      updated_at: new Date()
    };
    
    this.cartSignal.set(clearedCart);
    this.saveCartToStorage();
    
    console.log('🧹 Cart cleared');
  }

  /**
   * Verificar si el carrito pertenece al restaurante actual
   */
  isCartForRestaurant(restaurant_id: string, terminal_suffix: string): boolean {
    const currentCart = this.cartSignal();
    
    return currentCart ? 
      currentCart.restaurant_id === restaurant_id && 
      currentCart.terminal_suffix === terminal_suffix : 
      false;
  }

  /**
   * Obtener resumen del carrito para checkout
   */
  getCartSummary(): any {
    const currentCart = this.cartSignal();
    if (!currentCart) return null;

    return {
      restaurant_id: currentCart.restaurant_id,
      restaurant_name: currentCart.restaurant_name,
      terminal_suffix: currentCart.terminal_suffix,
      items: currentCart.items.map(item => ({
        product_id: item.product_id,
        name: item.name,
        quantity: item.quantity,
        unit_price: item.price,
        total_price: item.price * item.quantity,
        customizations: item.customizations,
        notes: item.notes
      })),
      total_items: this.totalItems(),
      total_price: this.totalPrice(),
      created_at: currentCart.created_at,
      updated_at: currentCart.updated_at
    };
  }

  /**
   * Generar ID único para item basado en producto y personalizaciones
   */
  private generateItemId(product_id: string, customizations: CartItemCustomization[], notes: string): string {
    const customStr = customizations
      .sort((a, b) => a.ingredient.localeCompare(b.ingredient))
      .map(c => `${c.type}:${c.ingredient}`)
      .join('|');
    
    const notesStr = notes.trim();
    const combined = `${product_id}_${customStr}_${notesStr}`;
    
    // Simple hash para generar ID corto pero único
    return btoa(combined).replace(/[^a-zA-Z0-9]/g, '').substring(0, 12);
  }

  /**
   * Guardar carrito en localStorage
   */
  private saveCartToStorage(): void {
    const cart = this.cartSignal();
    if (cart) {
      localStorage.setItem('barclik_cart', JSON.stringify(cart));
    }
  }

  /**
   * Cargar carrito desde localStorage
   */
  private loadCartFromStorage(): void {
    try {
      const stored = localStorage.getItem('barclik_cart');
      if (stored) {
        const cart = JSON.parse(stored);
        // Convertir fechas de string a Date
        cart.created_at = new Date(cart.created_at);
        cart.updated_at = new Date(cart.updated_at);
        
        this.cartSignal.set(cart);
        console.log('📦 Cart loaded from storage:', cart.items.length, 'items');
      }
    } catch (error) {
      console.error('❌ Error loading cart from storage:', error);
      localStorage.removeItem('barclik_cart');
    }
  }

  /**
   * Formatear precio
   */
  formatPrice(price: number): string {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  }

  /**
   * Obtener descripción de personalizaciones
   */
  getCustomizationsText(customizations: CartItemCustomization[]): string {
    if (customizations.length === 0) return '';
    
    return customizations.map(c => {
      switch (c.type) {
        case 'remove': return `Sin ${c.ingredient}`;
        case 'add': return `Extra ${c.ingredient}`;
        case 'change': return c.description;
        default: return c.description;
      }
    }).join(', ');
  }
}